import { QuestionCircle } from "@styled-icons/fa-solid/QuestionCircle";
import { isEmpty } from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import ReactTooltip from "components/ReactTooltip";
import { DataStoreContext } from "../../components/DataStore/DataStoreContext";
import {
  FrButton,
  FrError,
  FrInput,
  FrLabel,
  ToolbarContainer,
  ToolbarRight,
} from "../../components/DesignSystem/style";
import { FileLibrary, filenameToFileLibraryObjects } from "../../components/FileLibrary";
import { useFirebase } from "../../components/Firebase";
import Footer from "../../components/Footer";
import { createFormStore, DebugScenarioForm, FormContext, useForm, useFormField } from "../../components/FormProvider";
import { withAuthorization } from "../../components/Session";
import SettingsEditor from "../../components/SettingsEditor";
import { formatTranscriberProviderOptions, settings as defaultSettings } from "../../constants/defaults";
import { organizationSettingKeys } from "../../constants/settingsKeys";
import settings from "../../constants/schemas/settings.json";
import { checkManager, getUserType } from "../../helpFunctions/auth";
import { applyUpdateObject, compose, lodashToFirebaseUpdateObject } from "../../helpFunctions/general";
import { AdminNavigation, DetailedPageTitle } from "../Admin/AdminHeader";
import { AdminContainer } from "../Admin/style";
import { SettingWrapper } from "../ScenarioEditor/style";

// TODO currently no defaults or validation
const defaultOrganizationValue = {};
const orgValidationSchema = {
  properties: {
    displayName: { type: "string", normalize: "trim" },
    domain: { type: "string", normalize: "trim" },
    coverImage: { type: "string", normalize: "trim" },
    settings,
  },
};

export const OrganizationFormProvider = ({ selectedOrg, children }) => {
  // We are using vanilla (non-React) entrypoint to create the zustand store, then keep it as a reference
  // That means the store needs to be wrapped with useStore in a component to actually be reactive
  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = createFormStore(defaultOrganizationValue, orgValidationSchema);
  }

  return (
    <FormContext.Provider value={{ store: storeRef.current }}>
      <DebugScenarioForm />
      {children}
    </FormContext.Provider>
  );
};

const Organization = ({ state, dispatch, authUser }) => {
  const firebase = useFirebase();
  const isDirty = useForm(state => state.isDirty);
  const { store } = useContext(FormContext);
  const organizationDisplayNameField = useFormField("displayName");
  const coverImageField = useFormField("coverImage");
  const coverImageValue = coverImageField?.value;

  const [isSaving, setIsSaving] = useState(false);

  const transcriberProviderOptions = useMemo(
    () => formatTranscriberProviderOptions(defaultSettings.availableTranscriberProviders),
    []
  );

  const selectedFiles = useMemo(() => filenameToFileLibraryObjects(coverImageValue), [coverImageValue]);

  const coverImageSources = useMemo(() => {
    const sources = [];
    if (state.selectedOrg) {
      sources.push(
        firebase.createStorageSource({
          path: `organizations/${state.selectedOrg}/media`,
          name: "Organization library",
          upload: true,
          canDelete: true,
          shared: true,
        })
      );
    }
    return sources;
  }, [firebase, state.selectedOrg]);

  useEffect(() => {
    store.getState().setSavedData(state.organization);
  }, [state.organization, store]);

  const handleFileSelected = (files, options) => {
    coverImageField.onChange(files?.[0]?.id);
    // If the change was triggered by deleting the currently-selected file, the file is already
    // gone from Storage. This screen uses manual save, so persist the cleared reference
    // immediately to avoid leaving the persisted coverImage pointing at a deleted object.
    if (options?.deletedFile && state.selectedOrg) {
      firebase.updateOrganization(state.selectedOrg, { coverImage: null }).catch(err => {
        console.error("Failed to persist coverImage clear after deletion:", err);
        toast("Failed to update organization. Please save manually.", { type: "error" });
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const storeState = store.getState();
    const { isValid } = storeState.validate();
    if (!isValid) {
      toast("Some fields have errors. Please check all red fields before proceeding.", { type: "error" });
      setIsSaving(false);
      return;
    }

    let updateObject = storeState.getNormalizedChanges();
    delete updateObject.id;
    if (isEmpty(updateObject)) {
      console.log("No changes to save");
    } else {
      updateObject = lodashToFirebaseUpdateObject(updateObject);
      console.log(`Saving data to org ${state.selectedOrg}`, updateObject);
      try {
        await firebase.updateOrganization(state.selectedOrg, updateObject);
        dispatch({ type: "SET_ORGANIZATION", organization: applyUpdateObject(state.organization, updateObject) });
      } catch (error) {
        if (error?.message) {
          toast(error.message, { type: "error" });
        } else {
          toast("Failed to save organization. Please try again or contact support.", { type: "error" });
        }
        console.error("Error saving organization:", error);
      }
    }
    setIsSaving(false);
  };

  return (
    state.selectedOrg && (
      <AdminContainer>
        <ToastContainer />
        <DetailedPageTitle
          title={"Admin"}
          organization={state.organization}
          userType={getUserType(authUser?.claims, state.selectedOrg)}
        />
        <AdminNavigation />

        <div style={{ paddingTop: "24px", width: "100%" }}>
          <div style={{ maxWidth: "400px", width: "100%" }}>
            <SettingWrapper>
              <FrLabel>
                Organization ID (read-only)
                <QuestionCircle data-for="organizationId" data-tip={`The unique ID for this organization.`} />
                <ReactTooltip place="right" effect="solid" id="organizationId" html={true} />
              </FrLabel>
              <FrInput background={"var(--grey-5)"} readonly defaultValue={state.organization?.id} />
            </SettingWrapper>

            <SettingWrapper>
              <FrLabel htmlFor="settings.displayName">
                Display name
                <QuestionCircle data-for="displayName" data-tip={`The display name to show for this organization.`} />
                <ReactTooltip place="right" effect="solid" id="displayName" html={true} />
              </FrLabel>
              <FrInput {...organizationDisplayNameField.props} background={"var(--grey-5)"} placeholder="Display name" />
            </SettingWrapper>

            <SettingWrapper>
              <FrLabel htmlFor="settings.coverImage" style={{ marginBottom: "4px" }}>
                Cover image
                <QuestionCircle
                  data-for="coverImage-tooltip"
                  data-tip={`The image to show in the header, like an organizational logo.`}
                />
                <ReactTooltip place="right" effect="solid" id="coverImage-tooltip" html={true} />
              </FrLabel>

              <FileLibrary
                title="Select cover image"
                width={"120px"}
                sources={coverImageSources}
                multiple={false}
                onChange={handleFileSelected}
                selectedFiles={selectedFiles}
                fileTypeLabel="cover image"
              />
              {coverImageField.error && <FrError>{coverImageField.error}</FrError>}
            </SettingWrapper>
          </div>

          <SettingsEditor
            // Org settings inherit from global defaults. Doesn't make much sense to inherit from user here.
            defaultSettings={defaultSettings}
            settingKeys={organizationSettingKeys}
            fieldPath={"settings"}
            schema={orgValidationSchema.properties.settings.properties}
            extraSchema={{
              transcriberProvider: {
                default: "azure",
                enum: transcriberProviderOptions.map(option => option.id),
              },
            }}
            extraTranslations={{
              transcriberProvider: transcriberProviderOptions.reduce((acc, option) => {
                acc[option.id] = { title: option.name };
                return acc;
              }, {}),
            }}
          >
            <p>
              To change a preference, click to edit it and select its value. It will override default preferences or
              user preferences (but may be overridden by scenario specific preferences). Remove the preference if you
              no longer want to override.
            </p>
            <p>
              <strong>Warning</strong> these are advanced settings that may create unexpected behavior if not used
              correctly.
            </p>
          </SettingsEditor>
        </div>
        <Footer />
        <ToolbarContainer>
          <ToolbarRight>
            <FrButton padding={"9px 20px"} fontSize={"18px"} disabled={!isDirty || isSaving} onClick={handleSave}>
              {isSaving ? "Saving..." : isDirty ? "Save" : "Saved"}
            </FrButton>
          </ToolbarRight>
        </ToolbarContainer>
      </AdminContainer>
    )
  );
};

const OrganizationWrapper = props => {
  const { state, dispatch } = useContext(DataStoreContext);
  return (
    <OrganizationFormProvider id={state.selectedOrg} dispatch={dispatch}>
      <Organization state={state} dispatch={dispatch} {...props} />
    </OrganizationFormProvider>
  );
};

const condition = (authUser, state) => checkManager(authUser?.claims, state.selectedOrg);

export default compose(withAuthorization(condition))(OrganizationWrapper);
